import * as React from "react"
import { RiFilter3Line, RiArrowDownLine, RiArrowUpLine } from "react-icons/ri"
import {
  Box,
  VStack,
  Checkbox,
  Flex,
  Button,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  StatLabel,
  Stat,
  StatNumber,
  Center,
  HStack,
  Spinner,
  Stack,
} from "@chakra-ui/react"
import { ALERTS } from "../lib/static/alert"
import { AlertType, AlertEntity } from "../lib/types/alert"
import { BikeAlertsItem } from "./BikeAlertsItem"
import { NoData } from "./NoData"
import { Order } from "../lib/types/shared"
import { Bbox } from "../lib/helpers/map"
import { AlertsDownload } from "./AlertsDownload"
import { ZoneAlertsItem } from "./ZoneAlertsItem"
import { groupAlerts } from "../lib/helpers/alerts"

interface Props {
  count?: number
  alerts: AlertEntity[]
  refetchAlerts: () => Promise<any>
  defaultAlertFilters: AlertType[]
  alertFilters: AlertType[]
  onSetAlertFilters: (filters: AlertType[]) => void
  page: number
  pageSize: number
  onSetSelectedAlert: (uuid: string) => void
  onSetSelectedBike: (uuid: string) => void
  onSetPage: (page: number) => void
  order: Order
  onSetOrder: (value: Order) => void
  onSetBbox: (bbox: Bbox) => void
  isLoading: boolean
}

export const AlertsSidebar = (props: Props) => {
  const handleSetOrder = (order: Order["order"]) => {
    return props.onSetOrder({ order, orderBy: "created" })
  }

  const handleToggleFilter = (filter: AlertType) => {
    if (props.alertFilters.includes(filter)) {
      props.onSetAlertFilters(props.alertFilters.filter((f) => f !== filter))
    } else {
      props.onSetAlertFilters([...props.alertFilters, filter])
    }
  }

  const groupedAlerts = React.useMemo(() => groupAlerts(props.alerts), [props.alerts])

  const alertsListRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (alertsListRef.current) alertsListRef.current.scrollTo(0, 0)
  }, [props.page])

  const resultsFrom = !!props.count && props.page * props.pageSize - (props.pageSize - 1)
  const resultsTo =
    !!props.count && (props.page * props.pageSize > props.count ? props.count : props.page * props.pageSize)

  return (
    <Stack h="100%" px={6} py={4} pt={2} justify="space-between">
      <Box flexGrow={0} flexShrink={0}>
        <Stat>
          <StatLabel>Total alerts</StatLabel>
          <StatNumber>{props.count ?? <Spinner size="sm" />}</StatNumber>
        </Stat>
        <HStack justify="space-between">
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button size="sm" variant="outline" leftIcon={<Box as={RiFilter3Line} boxSize="18px" />}>
                {props.alertFilters.length} • Filters
              </Button>
            </PopoverTrigger>
            <PopoverContent zIndex={6}>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader alignItems="center">
                Alert types {props.isLoading && <Spinner ml={2} size="xs" />}
              </PopoverHeader>
              <PopoverBody pt={4}>
                <VStack align="flex-start" spacing={0}>
                  {props.defaultAlertFilters.map((alertFilter) => (
                    <Checkbox
                      key={alertFilter}
                      value={alertFilter}
                      onChange={(e) => handleToggleFilter(e.target.value as AlertType)}
                      isChecked={props.alertFilters.includes(alertFilter)}
                    >
                      <Text fontSize="0.9rem">{ALERTS[alertFilter as AlertType].label}</Text>
                    </Checkbox>
                  ))}
                </VStack>
              </PopoverBody>
              {props.alertFilters.length < props.defaultAlertFilters.length && (
                <PopoverFooter mt={2}>
                  <Flex justify="flex-end" align="center">
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => props.onSetAlertFilters(props.defaultAlertFilters)}
                    >
                      Select all
                    </Button>
                  </Flex>
                </PopoverFooter>
              )}
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleSetOrder(props.order.order === "-" ? "" : "-")}
            leftIcon={<Box as={props.order.order === "-" ? RiArrowDownLine : RiArrowUpLine} boxSize="18px" />}
          >
            {props.order.order === "-" ? "Newest" : "Oldest"}
          </Button>
          <AlertsDownload alerts={props.alerts} />
        </HStack>
      </Box>
      {props.isLoading ? (
        <Center h={200}>
          <Spinner />
        </Center>
      ) : props.alerts.length > 0 ? (
        <Box overflowY="scroll" flexGrow={1} ref={alertsListRef}>
          {Object.entries(groupedAlerts).map(([bicycleId, { alerts, bicycle, zone }], index) => {
            if (bicycle)
              return (
                <BikeAlertsItem
                  index={index}
                  key={bicycleId}
                  alerts={alerts}
                  bicycle={bicycle}
                  refetchAlerts={props.refetchAlerts}
                  onSelectAlert={() => {
                    if (!bicycle.location) return
                    props.onSetSelectedAlert(bicycle.bicycle_uuid)
                    props.onSetBbox([bicycle.location.coordinates, bicycle.location.coordinates])
                  }}
                  onSelectBike={() => {
                    if (!bicycle.bicycle_coordinates) return
                    props.onSetSelectedBike(bicycle.bicycle_uuid)
                    props.onSetBbox([bicycle.bicycle_coordinates, bicycle.bicycle_coordinates])
                  }}
                />
              )
            if (zone)
              return (
                <ZoneAlertsItem
                  index={index}
                  key={zone.zone_uuid}
                  alerts={alerts}
                  zone={zone}
                  refetchAlerts={props.refetchAlerts}
                  onSelect={() => {
                    if (!zone.location) return
                    props.onSetSelectedAlert(zone.zone_uuid)
                    props.onSetBbox([zone.location.coordinates, zone.location.coordinates])
                  }}
                />
              )
            return null
          })}
        </Box>
      ) : (
        <Center h={200}>
          <NoData>No Alerts</NoData>
        </Center>
      )}
      <Flex flexGrow={0} flexShrink={0} justify="space-between" align="center" my={2}>
        <Text color="gray.700" fontSize="0.8rem" py={4}>
          Showing{" "}
          {!!props.count ? (
            <>
              <Text as="span" fontWeight="bold">
                {resultsFrom}
              </Text>{" "}
              -{" "}
              <Text as="span" fontWeight="bold">
                {resultsTo}
              </Text>{" "}
              of{" "}
              <Text as="span" fontWeight="bold">
                {props.count}
              </Text>
            </>
          ) : (
            <Text as="span" fontWeight="bold">
              ...
            </Text>
          )}
        </Text>
        {props.count && props.count > props.pageSize && (
          <Stack direction="row" spacing={2} align="center">
            <Button onClick={() => props.onSetPage(props.page - 1)} isDisabled={props.page === 1}>
              Prev
            </Button>
            <Button
              onClick={() => props.onSetPage(props.page + 1)}
              isDisabled={props.page * props.pageSize > props.count}
            >
              Next
            </Button>
          </Stack>
        )}
      </Flex>
    </Stack>
  )
}
